/**
 * @fileoverview B43: the decision for an own to-do write that got no answer. The add's answer is a whole Today brief
 * built afresh, which on a slow server can pass the app's request limit although the to-do was saved. With no answer
 * at all (a timeout, a dropped connection; never a 4xx or 5xx), the app reads Today's brief again: the to-do is there,
 * so it counts as saved; it is not, so it is the standard failure. Pure, so it is tested without a screen.
 *
 * Where: useOwnTodos.
 * API: none (the caller reads the brief).
 *
 * @module own-todos/ownTodoLanded
 */

import axios from 'axios';
import type { OwnTodoChange } from '@/shared/api/ownTodo';
import type { OwnTodoItem } from '@/shared/api/todayBrief';

/** What was written, to look for it in the brief: a new to-do (with the ids listed before) or an edit. */
export type Written =
  | { kind: 'add'; title: string; beforeIds: number[] }
  | { kind: 'edit'; id: number; change: OwnTodoChange };

/** How a no-answer write turned out. */
export type Settled =
  | { result: 'added'; openKey: string }
  | { result: 'failure' }
  | { result: 'not-applicable' };

/**
 * Whether a failure is no answer at all (offline, dropped, timed out): an Axios error with no response, not cancelled.
 *
 * @param error - Whatever was thrown.
 * @returns True for no answer.
 */
export function isNoAnswer(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return false;
  return error.response === undefined;
}

/**
 * Finds the written to-do in the brief's own to-dos.
 *
 * @param written - What was added or edited.
 * @param items - The brief's own to-dos (todo.own), after the new read.
 * @returns The item, or null when it is not there.
 */
export function findLanded(written: Written, items: Array<Pick<OwnTodoItem, 'id' | 'key' | 'title' | 'planned'>>): { id: number; key: string } | null {
  if (written.kind === 'add') {
    const before = new Set(written.beforeIds);
    const hit = items.find((i) => !before.has(i.id) && i.title === written.title);
    return hit ? { id: hit.id, key: hit.key } : null;
  }
  const hit = items.find((i) => i.id === written.id);
  if (!hit) return null;
  const { title, when, day } = written.change;
  if (title !== undefined && hit.title !== title) return null;
  if (when !== undefined && hit.planned.when !== when) return null;
  if (when === 'day' && day !== undefined && hit.planned.day !== day) return null;
  return { id: hit.id, key: hit.key };
}

/**
 * Judges a write that failed: with no answer, reads the brief again and looks for the to-do; any answer (4xx, 5xx) is
 * left to the caller.
 *
 * @param error - What the write threw.
 * @param written - What was written.
 * @param reread - Reads Today's brief again and gives its own to-dos; may throw.
 * @returns added (it is there), failure (it is not, or the read failed), or not-applicable (an answer came).
 */
export async function settleAfterNoAnswer(error: unknown, written: Written, reread: () => Promise<OwnTodoItem[]>): Promise<Settled> {
  if (!isNoAnswer(error)) return { result: 'not-applicable' };
  try {
    const hit = findLanded(written, await reread());
    return hit ? { result: 'added', openKey: hit.key } : { result: 'failure' };
  } catch {
    return { result: 'failure' };
  }
}
